import { Modal, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  CatalogBrandCreateInput,
  CatalogBrandUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { BrandDTO } from "../api/client";
import {
  useCatalogBrandsQuery,
  useCreateCatalogBrand,
  useDeleteCatalogBrand,
  useUpdateCatalogBrand,
} from "../api/queries";
import { BrandForm } from "../ui/BrandForm";
import { CatalogBrandsFilter } from "../ui/CatalogBrandsFilter";
import { CatalogBrandsTable } from "../ui/CatalogBrandsTable";
import { CatalogBrandsToolbar } from "../ui/CatalogBrandsToolbar";

type CatalogBrandsSearch = {
  limit?: number;
  offset?: number;
  search?: string;
};

export function CatalogBrandsPage() {
  const nav = useNavigate({ from: "/app/catalog/brands" });
  const search = useSearch({
    from: "/app/catalog/brands",
  }) as CatalogBrandsSearch;

  const list = useCatalogBrandsQuery({
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    search: search.search,
  });
  const createBrand = useCreateCatalogBrand();
  const updateBrand = useUpdateCatalogBrand();
  const deleteBrand = useDeleteCatalogBrand();
  const canCreate = useActionPermission(["catalog:create"]);
  const canManage = useActionPermission(
    ["catalog:update", "catalog:delete"],
    "any",
  );

  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingBrand, setEditingBrand] = useState<BrandDTO | null>(null);

  const data = list.data?.data ?? [];
  const meta = list.data?.meta ?? { total: 0, limit: 20, offset: 0 };
  const isEdit = editingBrand != null;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog", to: "/app/catalog" },
            { label: "ຍີ່ຫໍ້ອາໄຫຼ່" },
          ]}
        />
      </Header>

      <Main>
        <CatalogBrandsToolbar
          onCreate={() => {
            setEditingBrand(null);
            setFormOpen(true);
          }}
          canCreate={!!canCreate}
        />

        <Modal
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) setEditingBrand(null);
          }}
          title={isEdit ? "ແກ້ໄຂແບຣນດ໌" : "ເພີ່ມແບຣນດ໌"}
          description={isEdit ? "ປັບປຸງຊື່ແບຣນດ໌" : "ສ້າງແບຣນດ໌ໃໝ່"}
          size="md"
        >
          <BrandForm
            key={editingBrand?.id ?? "new"}
            initialValues={
              editingBrand ? { name: editingBrand.name } : undefined
            }
            onSubmit={async (vals) => {
              try {
                setSubmitting(true);
                if (editingBrand) {
                  await updateBrand.mutateAsync({
                    id: editingBrand.id,
                    input: vals as CatalogBrandUpdateInput,
                  });
                  toast.success("ແກ້ໄຂແບຣນດ໌ສຳເລັດ");
                } else {
                  await createBrand.mutateAsync(
                    vals as CatalogBrandCreateInput,
                  );
                  toast.success("ເພີ່ມແບຣນດ໌ສຳເລັດ");
                }
                setFormOpen(false);
                setEditingBrand(null);
                await list.refetch();
              } catch (err: unknown) {
                const message =
                  (err as { message?: string })?.message ?? "ບັນທຶກບໍ່ສຳເລັດ";
                toast.error(message);
              } finally {
                setSubmitting(false);
              }
            }}
            onCancel={() => setFormOpen(false)}
            submitting={submitting}
          />
        </Modal>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CatalogBrandsFilter />

          <CatalogBrandsTable
            canManage={!!canManage}
            isLoading={list.isLoading}
            data={data}
            offset={meta.offset}
            limit={meta.limit}
            totalCount={meta.total}
            onEdit={(row) => {
              setEditingBrand(row);
              setFormOpen(true);
            }}
            onDelete={async (id) => {
              try {
                await deleteBrand.mutateAsync(id);
                toast.success("ລຶບແບຣນດ໌ສຳເລັດ");
                await list.refetch();
              } catch (err: unknown) {
                const msg =
                  err && typeof err === "object" && "message" in err
                    ? String((err as { message: string }).message)
                    : "ມີສິນຄ້າໃຊ້ແບຣນດ໌ນີ້ຢູ່ ບໍ່ສາມາດລຶບໄດ້";
                toast.error(msg);
              }
            }}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, limit, offset } })
            }
          />
        </div>
      </Main>
    </>
  );
}
